import React, { lazy, Suspense, useEffect, useRef, useState } from "react";
import Hero from "../Hero";

const SectionDivider = lazy(() => import("../SectionDivider"));
const About = lazy(() => import("../About"));
const Services = lazy(() => import("../Services"));
const Portfolio = lazy(() => import("../Portfolio"));
const Lab = lazy(() => import("../Lab"));
const Contact = lazy(() => import("../Contact"));

const SECTION_IDS = ["about", "services", "work", "lab", "contact"];

const SectionFallback = React.forwardRef(({ minHeight }, ref) => (
  <div
    ref={ref}
    className="bg-[#0f1115]"
    style={{ minHeight }}
    aria-hidden="true"
  />
));
SectionFallback.displayName = "SectionFallback";

const DeferredSection = ({ children, forceLoad = false, minHeight = "100svh" }) => {
  const placeholderRef = useRef(null);
  const [shouldLoad, setShouldLoad] = useState(forceLoad);

  useEffect(() => {
    if (forceLoad) setShouldLoad(true);
  }, [forceLoad]);

  useEffect(() => {
    if (shouldLoad || !placeholderRef.current) return undefined;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setShouldLoad(true);
          observer.disconnect();
        }
      },
      { rootMargin: "600px 0px" }
    );
    observer.observe(placeholderRef.current);

    return () => observer.disconnect();
  }, [shouldLoad]);

  if (!shouldLoad) {
    return <SectionFallback ref={placeholderRef} minHeight={minHeight} />;
  }

  return (
    <Suspense fallback={<SectionFallback minHeight={minHeight} />}>
      {children}
    </Suspense>
  );
};

const sectionIndexFromHash = (hash) =>
  SECTION_IDS.indexOf((hash || "").replace("#", ""));

const SectionWithDivider = ({ children }) => (
  <>
    <SectionDivider />
    {children}
  </>
);

const HomePage = () => {
  const [forceThroughIndex, setForceThroughIndex] = useState(() =>
    typeof window === "undefined"
      ? -1
      : sectionIndexFromHash(window.location.hash)
  );

  useEffect(() => {
    const revealHashTarget = () => {
      const index = sectionIndexFromHash(window.location.hash);
      if (index >= 0) {
        setForceThroughIndex((current) => Math.max(current, index));
      }
    };
    const onNav = (event) => {
      const link = event.target.closest?.('a[href^="#"]');
      if (!link) return;
      const index = sectionIndexFromHash(link.getAttribute("href"));
      if (index >= 0) {
        setForceThroughIndex((current) => Math.max(current, index));
      }
    };

    document.addEventListener("click", onNav, true);
    window.addEventListener("hashchange", revealHashTarget);

    return () => {
      document.removeEventListener("click", onNav, true);
      window.removeEventListener("hashchange", revealHashTarget);
    };
  }, []);

  return (
    <>
      <Hero />
      <DeferredSection forceLoad={forceThroughIndex >= 0}>
        <SectionWithDivider>
          <About />
        </SectionWithDivider>
      </DeferredSection>
      <DeferredSection forceLoad={forceThroughIndex >= 1}>
        <SectionWithDivider>
          <Services />
        </SectionWithDivider>
      </DeferredSection>
      <DeferredSection forceLoad={forceThroughIndex >= 2} minHeight="140svh">
        <SectionWithDivider>
          <Portfolio />
        </SectionWithDivider>
      </DeferredSection>
      <DeferredSection forceLoad={forceThroughIndex >= 3} minHeight="140svh">
        <SectionWithDivider>
          <Lab />
        </SectionWithDivider>
      </DeferredSection>
      <DeferredSection forceLoad={forceThroughIndex >= 4}>
        <SectionWithDivider>
          <Contact />
        </SectionWithDivider>
      </DeferredSection>
    </>
  );
};

export default HomePage;
